/**
 * The dashboard SPC tile's body. The bound tag's control chart comes from the server (the chart the analysis
 * chose for it, its limits and its signals: `/v1/twin/spc/*`), with the newest episodes under it. A tag the
 * analysis has not charted plots its latest readings without limits and says why. Nothing is estimated in the
 * browser.
 */
import { SpcEpisodes } from "@/components/twin/spc/SpcEpisodes";
import { errorDetailOf } from "@/lib/api/error";
import { spcNotChartedText, spcPickLocationChart, useSpcChartsForTag, useSpcSeries } from "@/lib/api/spc";
import { spcReferenceInUnit, spcSeriesInUnit } from "@/lib/api/spc-units";
import { useDisplayUnits } from "@/lib/api/display-units";
import { SPCChart, type SPCChartProps, type SPCPoint } from "@/lib/widgets/statistical/SPCChart";

export interface SpcTagChartProps {
  /** The bound tag (LiveTagDatum.tagId); absent in stub mode. */
  tagId?: string;
  /** The tag's latest readings, plotted without limits when the tag has no chart. */
  points: SPCPoint[];
  /** The unit the points are drawn in (the org's shown unit). */
  unit?: string;
  /**
   * The unit the tag stores, where the org's units converted the tag's readings (unit is then the shown one): the
   * server's chart and the golden reference are in it, and are moved into the shown unit here with the points.
   */
  storedUnit?: string;
  yMin?: number;
  yMax?: number;
  height?: number;
  reference?: SPCChartProps["reference"];
}

export function SpcTagChart({ tagId, points, storedUnit, ...given }: SpcTagChartProps) {
  const charts = useSpcChartsForTag(tagId);
  const picked = spcPickLocationChart(charts.data?.charts ?? []);
  const series = useSpcSeries(picked?.id);
  // The server's chart and the golden reference are in the tag's unit: they move into the shown one with the points,
  // never one without the other. Nothing moves for a metric org.
  const { shown: prefs } = useDisplayUnits();
  const stored = storedUnit ?? given.unit ?? "";
  const chart = { ...given, reference: spcReferenceInUnit(given.reference, stored, prefs) };
  const shown = series.data ? spcSeriesInUnit(series.data, stored, prefs).series : undefined;

  if (charts.isLoading || series.isLoading) return <SPCChart state={{ kind: "loading" }} {...chart} />;
  if (series.data && shown) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-2">
        <SPCChart series={shown} {...chart} className="min-h-0 flex-1" />
        <SpcEpisodes series={series.data} limit={3} className="max-h-[45%] shrink-0 overflow-y-auto" />
      </div>
    );
  }
  const failed = charts.error ?? series.error;
  let why: string | null = null;
  if (failed) {
    const detail = errorDetailOf(failed);
    why = detail ? `The control chart could not be loaded: ${detail}` : "The control chart could not be loaded.";
  } else if (tagId && charts.data) {
    why = spcNotChartedText(charts.data, tagId);
  }
  return (
    <div className="flex h-full min-h-0 flex-col gap-1.5">
      <SPCChart points={points} {...chart} className="min-h-0 flex-1" />
      {why && <p className="shrink-0 text-[11px] text-on-surface-muted">{why}</p>}
    </div>
  );
}
